import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { MapPin, Globe, CheckCircle2, Clock, XCircle, Zap, Search, Filter } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const VALUE_COLORS = {
  'Very High': 'bg-green-50 text-green-700 border border-green-200',
  'High': 'bg-blue-50 text-blue-700 border border-blue-200',
  'Medium': 'bg-yellow-50 text-yellow-700 border border-yellow-200',
  'Low': 'bg-gray-100 text-gray-600 border border-gray-200',
};

const STATUS_KEYS = {
  not_submitted: { icon: Globe, color: 'text-gray-500', bg: 'bg-gray-100', key: 'available' },
  queued: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', key: 'queued' },
  submitted: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50', key: 'submitted' },
  live: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', key: 'live' },
  failed: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', key: 'failed' },
};

export default function ClientCitations({ client, getHeaders }) {
  const { t } = useTranslation('dashboard');
  const [directories, setDirectories] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [express, setExpress] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState('live');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [creditBalance, setCreditBalance] = useState(999);

  const [blStatus, setBlStatus] = useState(null);
  const [blCitations, setBlCitations] = useState([]);

  const fetchData = useCallback(() => {
    Promise.all([
      fetch(`${API}/api/client/citations/directories`, { headers: getHeaders() }).then(r => r.json()),
      fetch(`${API}/api/client/citations/campaigns`, { headers: getHeaders() }).then(r => r.json()),
      fetch(`${API}/api/client/credits/balance`, { headers: getHeaders() }).then(r => r.json()).catch(() => ({ balance: 999 })),
      fetch(`${API}/api/client/citations/brightlocal/status`, { headers: getHeaders() }).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/client/citations/brightlocal/results`, { headers: getHeaders() }).then(r => r.json()).catch(() => ({ citations: [] })),
    ]).then(([dirs, camps, bal, bl, blRes]) => {
      setDirectories(dirs.directories || []);
      setCampaigns(camps.campaigns || []);
      setCreditBalance(bal.balance || 0);
      setBlStatus(bl);
      setBlCitations(blRes?.citations || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [getHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const categories = [...new Set(directories.map(d => d.category))];
  const filtered = directories
    .filter(d => filterCategory === 'all' || d.category === filterCategory)
    .filter(d => !searchQuery || d.name.toLowerCase().includes(searchQuery.toLowerCase()) || d.domain.toLowerCase().includes(searchQuery.toLowerCase()));

  const available = filtered.filter(d => d.status === 'not_submitted');
  const submitted = filtered.filter(d => d.status !== 'not_submitted');

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const selectAll = () => {
    if (selected.size === available.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(available.map(d => d.id)));
    }
  };

  const totalCost = selected.size * 2 + (express ? 15 : 0);

  const handleSubmit = async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/client/citations/campaign`, {
        method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_ids: [...selected], express }),
      });
      const d = await res.json();
      if (res.ok) {
        setSelected(new Set());
        setExpress(false);
        fetchData();
        setTab('campaigns');
      } else {
        alert(d.detail || 'Failed to create campaign');
      }
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="text-gray-400 animate-pulse py-12 text-center">{t('citations.loading')}</div>;

  const totalDirs = directories.length;
  const totalSubmitted = directories.filter(d => d.status !== 'not_submitted').length;
  const totalLive = directories.filter(d => d.status === 'live').length;

  return (
    <div className="space-y-6" data-testid="citations-tab">
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-5 text-center">
            <p className="text-xs text-gray-500 uppercase">{t('citations.totalDirectories')}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{totalDirs}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-5 text-center">
            <p className="text-xs text-gray-500 uppercase">{t('citations.submitted')}</p>
            <p className="text-3xl font-bold text-blue-400 mt-1">{totalSubmitted}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-5 text-center">
            <p className="text-xs text-gray-500 uppercase">{t('citations.liveCitations')}</p>
            <p className="text-3xl font-bold text-green-600 mt-1">{totalLive}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-5 text-center">
            <p className="text-xs text-gray-500 uppercase">{t('citations.included')}</p>
            <p className="text-lg font-bold text-blue-600 mt-2">{t('citations.withSubscription')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-50 rounded-lg p-1">
        {[
          { id: 'live', labelKey: 'tabLive' },
          { id: 'builder', labelKey: 'tabBuilder' },
          { id: 'status', labelKey: 'tabStatus' },
          { id: 'campaigns', labelKey: 'tabCampaigns' },
        ].map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`citations-tab-${tb.id}`}
            className={`px-4 py-2 rounded-md text-xs font-medium transition-all ${tab === tb.id ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t(`citations.${tb.labelKey}`)}
          </button>
        ))}
      </div>

      {/* Live Citations Tab (BrightLocal) */}
      {tab === 'live' && (
        <Card className="bg-white border-gray-200">
          <CardContent className="p-6">
            {blStatus?.configured && blCitations.length > 0 ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{t('citations.yourLiveCitations')}</h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {t('citations.foundAcross', { count: blCitations.length })}
                      {blStatus.last_sync && t('citations.lastChecked', { date: new Date(blStatus.last_sync).toLocaleDateString() })}
                    </p>
                  </div>
                  <div className="flex gap-2 text-xs">
                    <span className="bg-green-50 text-green-700 px-2 py-1 rounded-md font-medium">{blCitations.filter(c => c.status === 'live').length} {t('citations.live')}</span>
                    <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md font-medium">{blCitations.filter(c => c.status === 'found').length} {t('citations.found')}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  {blCitations.map((cite, i) => (
                    <div key={i} className="flex items-center gap-3 py-2.5 border-b border-gray-100 last:border-0">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cite.status === 'live' ? 'bg-green-500' : 'bg-blue-400'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900">{cite.directory}</p>
                          {cite.nap_correct && <CheckCircle2 className="w-3 h-3 text-green-500" />}
                          {cite.authority > 0 && <span className="text-[10px] text-gray-400">DA: {cite.authority}</span>}
                        </div>
                        {cite.url && (
                          <a href={cite.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline truncate block">{cite.url}</a>
                        )}
                      </div>
                      <Badge className={`text-[10px] ${cite.status === 'live' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'}`}>
                        {cite.status === 'live' ? t('citations.live') : t('citations.found')}
                      </Badge>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-10">
                <Globe className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <h3 className="text-sm font-semibold text-gray-900 mb-1">{t('citations.trackingTitle')}</h3>
                <p className="text-xs text-gray-500 max-w-sm mx-auto">
                  {blStatus?.configured
                    ? t('citations.trackingCollecting')
                    : t('citations.trackingPending')}
                </p>
                <p className="text-xs text-gray-400 mt-3">{t('citations.trackingMeantime')} <button className="text-blue-500 underline" onClick={() => setTab('builder')}>{t('citations.trackingMeantimeTab')}</button> {t('citations.trackingMeantimeRest')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Builder Tab */}
      {tab === 'builder' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
              <input type="text" placeholder={t('citations.searchPlaceholder')} value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 text-gray-900 rounded-lg pl-9 pr-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              <button onClick={() => setFilterCategory('all')} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filterCategory === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{t('citations.all')}</button>
              {categories.map(c => (
                <button key={c} onClick={() => setFilterCategory(c)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filterCategory === c ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{c}</button>
              ))}
            </div>
          </div>

          {/* Selection Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button size="sm" variant="outline" onClick={selectAll} className="text-xs border-gray-300 text-gray-600" data-testid="citation-select-all">
                {selected.size === available.length ? t('citations.deselectAll') : t('citations.selectAll', { count: available.length })}
              </Button>
              <span className="text-xs text-gray-400">{t('citations.selected', { count: selected.size })}</span>
            </div>
            {selected.size > 0 && (
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={express} onChange={e => setExpress(e.target.checked)}
                    className="rounded border-gray-200 bg-gray-50 text-amber-500" />
                  <span className="text-xs text-gray-600">{t('citations.express24')}</span>
                </label>
                <Button onClick={handleSubmit} disabled={submitting}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid="citation-submit-campaign">
                  <MapPin className="h-3.5 w-3.5 mr-1.5" /> {submitting ? t('citations.submitting') : t('citations.submitN', { count: selected.size })}
                </Button>
              </div>
            )}
          </div>

          {/* Directory Table */}
          <Card className="bg-white border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-[10px] text-gray-500 uppercase w-10" />
                    <th className="px-4 py-3 text-left text-[10px] text-gray-500 uppercase">{t('citations.tableDirectory')}</th>
                    <th className="px-4 py-3 text-left text-[10px] text-gray-500 uppercase">{t('citations.tableCategory')}</th>
                    <th className="px-4 py-3 text-center text-[10px] text-gray-500 uppercase">{t('citations.tableAuthority')}</th>
                    <th className="px-4 py-3 text-center text-[10px] text-gray-500 uppercase">{t('citations.tableValue')}</th>
                    <th className="px-4 py-3 text-center text-[10px] text-gray-500 uppercase">{t('citations.tableStatusCol')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(dir => {
                    const s = STATUS_KEYS[dir.status] || STATUS_KEYS.not_submitted;
                    const isAvailable = dir.status === 'not_submitted';
                    return (
                      <tr key={dir.id} className={`border-b border-gray-200/30 hover:bg-gray-100/20 transition-colors ${selected.has(dir.id) ? 'bg-blue-900/10' : ''}`}>
                        <td className="px-4 py-2.5">
                          {isAvailable ? (
                            <input type="checkbox" checked={selected.has(dir.id)} onChange={() => toggleSelect(dir.id)}
                              className="rounded border-gray-200 bg-gray-50 text-blue-500" />
                          ) : <s.icon className={`h-4 w-4 ${s.color}`} />}
                        </td>
                        <td className="px-4 py-2.5">
                          <p className="text-sm text-gray-900 font-medium">{dir.name}</p>
                          <p className="text-[10px] text-gray-500">{dir.domain}</p>
                        </td>
                        <td className="px-4 py-2.5"><Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-[10px]">{dir.category}</Badge></td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`text-sm font-bold ${dir.authority >= 90 ? 'text-green-600' : dir.authority >= 70 ? 'text-blue-600' : 'text-gray-500'}`}>
                            {dir.authority}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <Badge className={`${VALUE_COLORS[dir.value] || 'bg-gray-100 text-gray-600'} text-[10px]`}>{dir.value}</Badge>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <Badge className={`${s.bg} ${s.color} text-[10px]`}>{t(`citations.statusUi.${s.key}`)}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* Status Tab */}
      {tab === 'status' && (
        <div className="space-y-3">
          {submitted.length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t('citations.noStatusYet')}</p>
          ) : submitted.map((dir, i) => {
            const s = STATUS_KEYS[dir.status] || STATUS_KEYS.queued;
            return (
              <Card key={i} className="bg-white border-gray-200">
                <CardContent className="pt-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <s.icon className={`h-5 w-5 ${s.color}`} />
                    <div>
                      <p className="text-sm text-gray-900 font-medium">{dir.name}</p>
                      <p className="text-[10px] text-gray-500">{dir.domain}</p>
                    </div>
                  </div>
                  <Badge className={`${s.bg} ${s.color} text-xs`}>{t(`citations.statusUi.${s.key}`)}</Badge>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Campaigns Tab */}
      {tab === 'campaigns' && (
        <div className="space-y-3">
          {campaigns.length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t('citations.noCampaigns')}</p>
          ) : campaigns.map((camp, i) => (
            <Card key={i} className="bg-white border-gray-200">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-900 font-medium">{t('citations.campaignTitle', { id: camp.campaign_id })}</p>
                    <p className="text-xs text-gray-500">{t('citations.campaignSites', { sites: camp.sites })} {camp.express && <Badge className="bg-amber-50 text-amber-600 text-[9px] ml-1">{t('citations.express')}</Badge>}</p>
                    <p className="text-[10px] text-gray-500">{camp.created_at ? new Date(camp.created_at).toLocaleString() : ''}</p>
                  </div>
                  <div className="text-right">
                    <Badge className={camp.status === 'processing' ? 'bg-blue-50 text-blue-600 text-xs' : 'bg-green-50 text-green-600 text-xs'}>{camp.status}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
